import { CommunityViewModel } from "client/Api/Communities/communityPosts";
import {
	FormattedText,
	InlineOptionSelector,
	InlineOptionSelectorItem,
	Typography
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { isNil } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useMemo } from "react";
import styles from "./CommunityInlineSelector.module.less";

type CommunityInlineSelectorProps = {
	attachable: IValueInputAttachable<number | null>;
	communities: readonly CommunityViewModel[];
	isReadOnly?: boolean;
};

export const CommunityInlineSelector = (
	props: CommunityInlineSelectorProps
): ReactElement | null => {
	const {
		attachable,
		communities,
		isReadOnly = false
	} = props;

	const selectOptions = useMemo(
		(): readonly InlineOptionSelectorItem<number | null>[] => {
			return [
				{
					text: localize("_NotSelected"),
					value: null
				},
				...communities.map(x => ({
					text: x.name,
					value: x.id
				}))
			];
		},
		[communities]
	);

	const selectedCommunityName = useMemo(
		(): string | undefined => {
			return selectOptions.find(x => x.value === attachable.value)
				?.text;
		},
		[
			selectOptions,
			attachable.value
		]
	);

	if (isReadOnly) {
		if (isNil(selectedCommunityName)) {
			return null;
		}

		return (
			<div className={styles.readonlyText}>
				<Typography textColor="secondary">
					<FormattedText
						text={localize("_PostedInName")}
						thingsToReplace={[{
							what: "{{Name}}",
							filler: selectedCommunityName
						}]}
						thingsToWrap={[{
							blockId: "Name",
							wrap: (x) => (
								<Typography
									weight="bold"
								>
									{x}
								</Typography>
							)
						}]}
					/>
				</Typography>
			</div>
		);
	}

	return (
		<div className={styles.root}>
			<div className={styles.label}>
				{localize("_SelectACommunity")}
			</div>
			<InlineOptionSelector
				{...attachable.attachToSelect()}
				options={selectOptions}
				menuTitle={localize("_SelectACommunity")}
			/>
		</div>
	);
};
CommunityInlineSelector.displayName = "CommunityInlineSelector";